import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { styled } from '@mui/material'
import { intl } from '../../../../../../../../../i18n'
import {
  ProgressReportByTeamPageConfig,
  ProgressReportScopeType,
} from '../../../../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ProcessTreeSelect } from '../../../../ProjectPlanSelect/ProcessTreeSelect'
import { TicketListSelect } from '../../../../TicketListSelect'
import { TeamTreeSelect } from '../../../../TeamSelect/TeamTreeSelect'
import ExclusiveToggleGroup from '../../../../../../../../../view/components/editors/toggle/ExclusiveToggleGroup'
import { usePageConfigHandlers } from '../hooks/usePageConfigHandlers'
import { useRootNodeDisplayName } from '../../../../../../hooks/useRootNodeDisplayName'
import { buildTeamPageTitle } from '../../../../../../lib/teamPageTitle'
import { PageTitleSuggestionButton } from './PageTitleSuggestionButton'
import {
  ConfigContainer,
  SectionContent,
  PageTitleSection,
  FormSection,
  FormLabel,
  PageTitleInput,
} from '../styles'
import { VersionReportVersionPageFormModel } from '../../../model/VersionReportVersionPageFormModel'

type ConfigFormProps = {
  page: VersionReportVersionPageFormModel
  snapshotUuid: string
  onUpdate: (
    pageNumber: number,
    updater: (
      prev: VersionReportVersionPageFormModel
    ) => VersionReportVersionPageFormModel
  ) => void
}

const SCOPE_TYPE_OPTIONS = [
  {
    value: 'PROCESS',
    label: intl.formatMessage({
      id: 'versionReport.pageConfig.scopeType.process',
    }),
  },
  {
    value: 'TICKET_LIST',
    label: intl.formatMessage({
      id: 'versionReport.pageConfig.scopeType.ticketList',
    }),
  },
]

const isProgressReportScopeType = (
  value: string
): value is ProgressReportScopeType =>
  value === 'PROCESS' || value === 'TICKET_LIST'

export const ProgressReportByTeamConfigForm = ({
  page,
  snapshotUuid,
  onUpdate,
}: ConfigFormProps) => {
  const config = useMemo(
    () => page.config as ProgressReportByTeamPageConfig,
    [page.config]
  )

  const { handlePageTitleChange } = usePageConfigHandlers(page, onUpdate)

  const { displayName: rootName } = useRootNodeDisplayName({
    snapshotUuid,
    scopeType: config.scopeType,
    rootUuid: config.rootUuid,
  })
  const suggestedTitle = rootName ? buildTeamPageTitle(rootName) : undefined
  const handleApplySuggestedTitle = () => {
    if (!suggestedTitle) return
    onUpdate(page.pageNumber, prev => ({ ...prev, pageTitle: suggestedTitle }))
  }

  // 工程/チケット管理表は切替でコンポーネントが入れ替わるため、開閉状態はここで保持する
  const [isScopeSelectOpen, setIsScopeSelectOpen] = useState(false)
  const scopeSelectOpenState = useMemo(
    () => ({
      isOpen: isScopeSelectOpen,
      onToggle: () => setIsScopeSelectOpen(prev => !prev),
    }),
    [isScopeSelectOpen]
  )

  const lastRootUuidByScopeRef = useRef<
    Partial<Record<ProgressReportScopeType, string>>
  >({})

  useEffect(() => {
    if (config.rootUuid) {
      lastRootUuidByScopeRef.current[config.scopeType] = config.rootUuid
    }
  }, [config.scopeType, config.rootUuid])

  const handleRootUuidChange = useCallback(
    (uuids: string[]) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByTeamPageConfig
        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            rootUuid: uuids[0],
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  const handleScopeTypeChange = useCallback(
    (value: string) => {
      if (!isProgressReportScopeType(value)) return
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByTeamPageConfig
        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            scopeType: value,
            rootUuid: lastRootUuidByScopeRef.current[value],
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  const handleTeamUuidsChange = useCallback(
    (uuids: string[]) => {
      onUpdate(page.pageNumber, prevEntity => {
        const currentConfig =
          prevEntity.config as ProgressReportByTeamPageConfig
        return {
          ...prevEntity,
          config: {
            ...currentConfig,
            tableRows: uuids,
          },
        }
      })
    },
    [onUpdate, page.pageNumber]
  )

  return (
    <ConfigContainer>
      {/*
        ページタイトルはスクロール領域の外に置く。下の集計範囲やチームを選ぶあいだも
        見えていてほしいのと、候補を反映した結果がその場で確認できるようにするため
      */}
      <PageTitleSection>
        <PageTitleRow>
          <TeamPageTitleInput
            label={intl.formatMessage({
              id: 'versionReport.pageConfig.pageTitle',
            })}
            value={page.pageTitle}
            onChange={handlePageTitleChange}
            placeholder={intl.formatMessage({
              id: 'versionReport.pageConfig.pageTitle.placeholder',
            })}
            variant="outlined"
            required={true}
          />
          <PageTitleSuggestionButton
            suggestedTitle={suggestedTitle}
            isApplied={page.pageTitle === suggestedTitle}
            onApply={handleApplySuggestedTitle}
          />
        </PageTitleRow>
      </PageTitleSection>
      <SectionContent>
        <FormSection>
          <FormLabel>
            {intl.formatMessage({ id: 'versionReport.pageConfig.scopeType' })}
          </FormLabel>
          <ExclusiveToggleGroup
            value={config.scopeType}
            options={SCOPE_TYPE_OPTIONS}
            onChange={handleScopeTypeChange}
          />
        </FormSection>

        {config.scopeType === 'TICKET_LIST' ? (
          <FormSection>
            <FormLabel>
              {intl.formatMessage({
                id: 'versionReport.pageConfig.scope.ticketList',
              })}
            </FormLabel>
            <TicketListSelect
              key={page.pageNumber}
              snapshotUuid={snapshotUuid}
              value={config.rootUuid ? [config.rootUuid] : []}
              onChange={handleRootUuidChange}
              height="280px"
              openState={scopeSelectOpenState}
            />
          </FormSection>
        ) : (
          <FormSection>
            <FormLabel>
              {intl.formatMessage({
                id: 'versionReport.pageConfig.scope.process',
              })}
            </FormLabel>
            <ProcessTreeSelect
              key={page.pageNumber}
              snapshotUuid={snapshotUuid}
              value={config.rootUuid ? [config.rootUuid] : []}
              onChange={handleRootUuidChange}
              multiSelect={false}
              height="280px"
              collapsible={true}
              openState={scopeSelectOpenState}
            />
          </FormSection>
        )}

        <FormSection>
          <FormLabel>
            {intl.formatMessage({ id: 'versionReport.pageConfig.teams' })}
          </FormLabel>
          <TeamTreeSelect
            key={page.pageNumber}
            snapshotUuid={snapshotUuid}
            value={config.tableRows}
            onChange={handleTeamUuidsChange}
            rows={page.rows}
            height="280px"
          />
        </FormSection>
      </SectionContent>
    </ConfigContainer>
  )
}

const PageTitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

// 候補が長いときに入力欄ではなく候補側を省略させる
const TeamPageTitleInput = styled(PageTitleInput)({
  flexShrink: 0,
})
